import * as d3 from "d3";
import React, {useEffect, useRef, useState} from "react";
import {secondsToHm} from "../../UsersStatisticsDashboard";

const CHART_HEIGHT = 350;
const CHART_MARGIN = {top: 0, right: 20, bottom: 20, left: 150};
const MIN_BAR_WIDTH = 5;

interface IDataItem {
    "username": string,
    "from": string,
    "to"?: string,
    "companyRef": string,
    "jobTitle"?: string,
}

interface IUserStatisticsGanttChartProps {
    userSessionData: IDataItem[];
    groupingType: string;
    companyFilter: string | undefined;
    jobTitleFilter: string | undefined;
}

const parseDate = (dateString: string | undefined) => {
    if (dateString === undefined) {
        return new Date();
    }
    return new Date(dateString);
};

const UserStatisticsGanttChart = ({userSessionData, groupingType, companyFilter, jobTitleFilter}: IUserStatisticsGanttChartProps) => {
    const svgRef = useRef(null);
    const tooltipRef = useRef(null);
    const [ganttChartData, setGanttChartData] = useState([])

    // TODO do it in a not so hacky way
    useEffect(() => {
        if (groupingType === "jobTitle") {
            const tempData = structuredClone(userSessionData);
            tempData.forEach((user: any) => {
                if(user.jobTitle === undefined) {
                    user["jobTitle"] = "No Title"
                }
                delete user["username"]
                user["username"] = user.jobTitle
            })

            setGanttChartData(tempData)

        } else {
            setGanttChartData(userSessionData)
        }
    }, [ groupingType, companyFilter, jobTitleFilter, userSessionData]);


    function tooltipHandler(event, d) { // Add mouseover event
        const sessionTime = (parseDate(d.to) - parseDate(d.from)) / 1000; // Convert to seconds
        const sessionTimeFormat = secondsToHm(sessionTime); // Format to HH:mm:ss
        const startTimeFormat = d3.timeFormat("%H:%M:%S")(parseDate(d.from));
        const endTimeFormat = d3.timeFormat("%H:%M:%S")(parseDate(d.to));
        d3.select(tooltipRef.current)
            .style("left", event.pageX + "px")
            .style("top", event.pageY + "px")
            .style("opacity", 1)
            .html(`User: ${d.username}<br>Start: ${startTimeFormat}<br>End: ${endTimeFormat}<br>Session Time: ${sessionTimeFormat}`);
    }


    useEffect(() => {
        // Set up chart dimensions
        let width = svgRef.current.parentElement.offsetWidth - CHART_MARGIN.left - CHART_MARGIN.right;
        const height = CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom;

        // Create SVG container
        const svgContainer = d3
            .select(svgRef.current)
            .attr("width", width + CHART_MARGIN.left + CHART_MARGIN.right)
            .attr("height", height + CHART_MARGIN.top + CHART_MARGIN.bottom);

        svgContainer.on("dblclick", () => {
            if (minDate !== undefined && maxDate !== undefined) xScale.domain([minDate, maxDate]);

            svg.select(".x.axis").call(xAxis as any);
            barsLayer.selectAll("rect")
                .attr("x", d => xScale(parseDate(d.from) as Date))
                .attr("width", (d) => Math.max(xScale(parseDate(d.to) as any) - xScale(parseDate(d.from) as any), MIN_BAR_WIDTH))
        });

        const svg = svgContainer
            .append("g")
            .attr("transform", `translate(${CHART_MARGIN.left},${CHART_MARGIN.top})`);


        let xScale = d3.scaleTime().domain([new Date(), new Date()]).range([0, width]);
        let minDate = d3.min(ganttChartData, (d) => parseDate(d.from));
        let maxDate = d3.max(ganttChartData, (d) => parseDate(d.to));

        if (minDate !== undefined && maxDate !== undefined) {
            xScale = d3.scaleTime().domain([minDate, maxDate]).range([0, width]);
        }

        const yScale = d3
            .scaleBand()
            .domain(ganttChartData.map((d) => d.username))
            .range([0, height])
            .padding(0.1);

        // Define color scale
        const colorScale = d3.scaleOrdinal(d3.schemeCategory10).domain(ganttChartData.map((d) => d.username));

        const brushLayer = svg.append("g").attr("class", "brush-layer");
        const barsLayer = svg.append("g").attr("class", "bars-layer");

        // Draw Gantt bars
        barsLayer
            .selectAll("rect")
            .data(ganttChartData)
            .enter()
            .append("rect")
            .attr("x", (d) => xScale(parseDate(d.from) as any))
            .attr("y", (d) => yScale(d.username) || 0) // Ensure it always returns a number
            .attr("width", (d) => Math.max(xScale(parseDate(d.to) as any) - xScale(parseDate(d.from) as any), MIN_BAR_WIDTH))
            .attr("height", yScale.bandwidth())
            .attr("fill", (d) => colorScale(d.username))
            .on("mouseover", tooltipHandler)
            .on("mouseout", function () { // Add mouseout event
                d3.select(tooltipRef.current)
                    .style("opacity", 0);
            });

        // Draw white background to the left of Y-axis
        svg.append("rect")
            .attr("x", 0 - CHART_MARGIN.left)
            .attr("y", 0)
            .attr("width", CHART_MARGIN.left)
            .attr("height", height + CHART_MARGIN.bottom)
            .attr("fill", "white");

        // Draw axes
        const xAxis = d3.axisBottom(xScale);
        const yAxis = d3.axisLeft(yScale);


        svg.append("g").attr("transform", `translate(0,${height})`).call(xAxis as any);
        svg.append("g").call(yAxis as any);


        let brush = d3.brushX()
            .extent([[0, 0], [width, height]])
            .on("end", brushed);

        brushLayer
            .call(brush);

        function brushed(event) {
            if (event.selection) {
                let extent = event.selection;

                // Update xScale domain based on selected area
                xScale.domain([xScale.invert(extent[0]), xScale.invert(extent[1])]);

                // Redraw the chart elements with updated scales
                svg.select(".x.axis").call(xAxis as any);
                barsLayer.selectAll("rect")
                    .attr("x", d => xScale(parseDate(d.from) as Date))
                    .attr("width", (d) => Math.max(xScale(parseDate(d.to) as any) - xScale(parseDate(d.from) as any), MIN_BAR_WIDTH))

                // Clear the brush selection
                brushLayer.call(brush.move, null);
            }
        }


        // Cleanup on component unmount
        return () => {
            svg.selectAll("*").remove();
        };
    }, [ganttChartData]);


    return (
        <div>
            <svg ref={svgRef}></svg>
            <div ref={tooltipRef} style={{
                position: "absolute",
                zIndex: 10,
                opacity: 0,
                backgroundColor: "white",
                color: "black",
                padding: "2px",
                borderRadius: "2px"
            }}>
            </div>
        </div>
    );

};

export default UserStatisticsGanttChart;